import { useCallback, useEffect, useState } from "react";
import { Clock3, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Empty } from "@/components/common";
import { api, errMsg, fmtDate } from "@/lib/api";

export function RecentlyBookedView({ onOpen, onToggleStar }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    setError("");
    api
      .get("/practicals/recently-booked")
      .then((response) => setItems(response.data.items || []))
      .catch((requestError) => {
        setItems([]);
        setError(errMsg(requestError));
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!items) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="recently-booked-loading">
        Loading…
      </p>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-destructive" data-testid="recently-booked-error">
        {error}
      </p>
    );
  }

  if (items.length === 0) {
    return <Empty>No current or previous bookings to show yet.</Empty>;
  }

  return (
    <ol
      className="divide-y overflow-hidden rounded-xl border bg-card"
      data-testid="recently-booked-list"
    >
      {items.map((practical, index) => (
        <li key={practical.id} className="flex items-center gap-3 px-4 py-3">
          <span
            className="w-6 text-center font-display text-lg font-bold text-muted-foreground"
            data-testid={`recently-booked-rank-${practical.id}`}
          >
            {index + 1}
          </span>
          <button
            type="button"
            onClick={() => onOpen(practical.id)}
            className="min-w-0 flex-1 text-left"
            data-testid={`recently-booked-practical-${practical.id}`}
          >
            <span className="block truncate font-medium">{practical.name}</span>
            <span className="flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
              <span>{[practical.subject, practical.topic].filter(Boolean).join(" · ")}</span>
              <span className="inline-flex items-center gap-1">
                <Clock3 className="h-3 w-3" />
                Last booked {fmtDate(practical.last_booked_at)}
              </span>
            </span>
          </button>
          <span
            className="shrink-0 text-sm font-semibold"
            data-testid={`recently-booked-count-${practical.id}`}
          >
            {practical.booking_count}×
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => onToggleStar(practical, load)}
            aria-label={`${practical.starred ? "Unstar" : "Star"} ${practical.name}`}
            data-testid={`recently-booked-toggle-${practical.id}`}
          >
            <Star
              className={`h-4 w-4 ${
                practical.starred ? "fill-amber-400 text-amber-500" : ""
              }`}
            />
          </Button>
        </li>
      ))}
    </ol>
  );
}